import { Check, Gift, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useSubscriptionTiers, type TierInfo, type PromoCodeValidation } from '@/api'

type StepSubscribeProps = {
  onSubscribe: (tier: 'SOLO' | 'PRO' | 'BUSINESS') => void
  onRedeemPromo?: () => void
  isLoading: boolean
  promoValidation?: PromoCodeValidation
  promoLoading?: boolean
  promoRedeeming?: boolean
  promoError?: string
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`
}


type PricingCardProps = {
  tier: TierInfo
  isPopular?: boolean
  isRecommended?: boolean
  onSubscribe: (tier: 'SOLO' | 'PRO' | 'BUSINESS') => void
  isLoading: boolean
}

function PricingCard({ tier, isPopular, isRecommended, onSubscribe, isLoading }: PricingCardProps) {
  return (
    <Card className={`relative flex flex-col ${isPopular ? 'border-primary shadow-lg' : ''}`}>
      {isRecommended && (
        <Badge className="absolute -top-2.5 left-3 text-xs" variant="default">
          Recommended
        </Badge>
      )}
      {tier.tier === 'SOLO' && (
        <Badge className="absolute -top-2.5 right-3 text-xs" variant="secondary">
          30-day free trial
        </Badge>
      )}
      <CardHeader className="text-center pb-2 pt-5">
        <CardTitle className="text-lg">{tier.name}</CardTitle>
        <CardDescription className="text-muted-foreground text-xs">
          {tier.tier === 'SOLO' && 'Perfect for personal use'}
          {tier.tier === 'PRO' && 'For professionals with multiple inboxes'}
          {tier.tier === 'BUSINESS' && 'For teams and power users'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="text-center mb-3">
          <span className="text-2xl font-bold">{formatPrice(tier.price_cents)}</span>
          <span className="text-muted-foreground text-sm">/month</span>
        </div>
        <div className="space-y-1 mb-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-medium">{tier.email_limit.toLocaleString()}</span>
            <span className="text-muted-foreground">emails/month</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{tier.inbox_limit}</span>
            <span className="text-muted-foreground">{tier.inbox_limit === 1 ? 'inbox' : 'inboxes'}</span>
          </div>
        </div>
        <ul className="space-y-1">
          {tier.features.slice(0, 4).map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button
          className="w-full"
          variant={isPopular ? 'default' : 'secondary'}
          disabled={isLoading}
          onClick={() => onSubscribe(tier.tier)}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : tier.tier === 'SOLO' ? (
            'Start Free Trial'
          ) : (
            'Subscribe'
          )}
        </Button>
      </CardFooter>
    </Card>
  )
}

function PricingCardSkeleton() {
  return (
    <Card className="flex flex-col">
      <CardHeader className="text-center pb-2">
        <Skeleton className="h-6 w-20 mx-auto mb-2" />
        <Skeleton className="h-4 w-40 mx-auto" />
      </CardHeader>
      <CardContent className="flex-1">
        <div className="text-center mb-4">
          <Skeleton className="h-8 w-16 mx-auto" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      </CardContent>
      <CardFooter>
        <Skeleton className="h-10 w-full" />
      </CardFooter>
    </Card>
  )
}

export function StepSubscribe({ 
  onSubscribe, 
  onRedeemPromo, 
  isLoading, 
  promoValidation, 
  promoLoading,
  promoRedeeming,
  promoError,
}: StepSubscribeProps) {
  const { data: tiers, isLoading: tiersLoading } = useSubscriptionTiers()
  
  // Show loading while validating promo code
  if (promoLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    )
  }
  
  // Show invite UI if valid promo code is present
  if (promoValidation?.is_valid && onRedeemPromo) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Gift className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">You've Been Invited!</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            You're getting {promoValidation.tier_name} access - completely free.
          </p>
        </div>
        
        {promoError && (
          <Alert variant="destructive" className="max-w-md mx-auto">
            <AlertDescription>{promoError}</AlertDescription>
          </Alert>
        )}
        
        <Card className="relative max-w-md mx-auto border-primary shadow-lg">
          <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs" variant="default">
            Free Access
          </Badge>
          <CardHeader className="text-center pb-2 pt-5">
            <CardTitle className="text-xl">{promoValidation.tier_name} Plan</CardTitle>
            <CardDescription className="text-muted-foreground">
              Full access to all {promoValidation.tier_name} features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-4">
              <span className="text-3xl font-bold">$0</span>
              <span className="text-muted-foreground text-sm">/month</span>
            </div>
            <ul className="space-y-2">
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-500 shrink-0" />
                <span>{promoValidation.email_limit?.toLocaleString()} emails/month</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-500 shrink-0" />
                <span>{promoValidation.inbox_limit} {promoValidation.inbox_limit === 1 ? 'inbox' : 'inboxes'}</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-500 shrink-0" />
                <span>AI-powered email processing</span>
              </li>
              <li className="flex items-center gap-2 text-sm">
                <Check className="h-4 w-4 text-green-500 shrink-0" />
                <span>Custom instructions</span>
              </li>
            </ul>
          </CardContent>
          <CardFooter>
            <Button
              className="w-full"
              size="lg"
              disabled={promoRedeeming}
              onClick={onRedeemPromo}
            >
              {promoRedeeming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Activating...
                </>
              ) : (
                'Activate Free Access'
              )}
            </Button>
          </CardFooter>
        </Card>
        
        <p className="text-center text-sm text-muted-foreground">
          No credit card required. Your access is completely free.
        </p>
      </div>
    )
  }
  
  // Show regular pricing cards
  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">Choose Your Plan</h1>
        <p className="text-muted-foreground max-w-md mx-auto">
          Select a plan to activate your inbox. The Solo plan includes a 30-day free trial.
        </p>
      </div>
      
      {/* Show error if promo code was invalid */}
      {promoValidation && !promoValidation.is_valid && promoValidation.error && (
        <Alert variant="destructive" className="max-w-md mx-auto">
          <AlertDescription>{promoValidation.error}</AlertDescription>
        </Alert>
      )}
      
      <div className="grid gap-4 md:grid-cols-3">
        {tiersLoading ? (
          <>
            <PricingCardSkeleton />
            <PricingCardSkeleton />
            <PricingCardSkeleton />
          </>
        ) : (
          tiers?.map((tier) => (
            <PricingCard
              key={tier.tier}
              tier={tier}
              isPopular={tier.tier === 'SOLO'}
              isRecommended={tier.tier === 'SOLO'}
              onSubscribe={onSubscribe}
              isLoading={isLoading}
            />
          ))
        )}
      </div>
      
      <p className="text-center text-sm text-muted-foreground">
        Solo includes a 30-day free trial. Cancel anytime. All plans include AI-powered email processing.
      </p>
    </div>
  )
}
